/**
 * Extracts the restriction number from a restriction name.
 * For context, canned restrictions all have names with the pattern "Restriction 1", "Restriction 2", "Restriction 3", etc.
 * We want to sort them numerically instead of alphabetically.
 * @param {string} restrictionName - The name of the restriction.
 * @returns {number|undefined} The extracted restriction number, or undefined if not found.
 */
export function extractRestrictionNumber(restrictionName) {
  const match = /Restriction (\d+)/.exec(restrictionName)?.[1];
  return match ? +match : undefined;
};

/**
 * Returns a comparer function for sorting restrictions.
 * For context, canned restrictions all have names with the pattern "Restriction 1", "Restriction 2", "Restriction 3", etc.
 * We want to sort them numerically instead of alphabetically.
 * Some restrictions may also have a customIndex property that specifies a custom sort order. If both restrictions have a 
 * custom index, it will be used over anything else.
 * @param {string|function} [nameSelector='restrictionName'] - The property name or function to extract the restriction name.
 * @param {string|function} [customIndexSelector='customIndex'] - The property name or function to extract the custom index.
 * @returns {function} A comparer function that can be used with Array.prototype.sort.
 * @example restrictions.sort(getRestrictionComparer());
 * @example restrictions.sort(getRestrictionComparer('restrictionName'));
 * @example restrictions.sort(getRestrictionComparer((r) => r.restrictionName));
 * @example restrictions.sort(getRestrictionComparer('restrictionName', 'customIndex'));
 * @example restrictions.sort(getRestrictionComparer('restrictionName', (r) => r.customIndex));
 */
export function getRestrictionComparer(nameSelector = 'restrictionName', customIndexSelector = 'customIndex') {
  return function(restrictionA, restrictionB) {
    if (customIndexSelector != null) {
      const customIndexExtractor = typeof customIndexSelector === 'function'
        ? customIndexSelector
        : (restriction) => restriction[customIndexSelector];

      const customIndexA = customIndexExtractor(restrictionA);
      const customIndexB = customIndexExtractor(restrictionB);

      if (customIndexA != null && customIndexB != null) {
        return customIndexA - customIndexB;
      }
    }

    if (nameSelector != null) {
      const nameExtractor = typeof nameSelector === 'function'
        ? nameSelector
        : (restriction) => restriction[nameSelector];

      const nameA = nameExtractor(restrictionA);
      const nameB = nameExtractor(restrictionB);

      if (nameA == null && nameB == null) { return 0; }
      if (nameA == null) { return -1; }
      if (nameB == null) { return 1; }
  
      const numberA = extractRestrictionNumber(nameA);
      const numberB = extractRestrictionNumber(nameB);
  
      if (numberA != null && numberB != null) {
        return numberA - numberB;
      }
      if (numberA != null) { return -1; }
      if (numberB != null) { return 1; }

      return !isNaN(nameA) && !isNaN(nameB)
        ? Number(nameA) - Number(nameB)
        : String(nameA).localeCompare(String(nameB));
    }

    return 0;
  };
}
